import { Component } from '@theme/component';

/**
 * Custom element that augments the APC mobile menu drawer.
 *
 * The drawer itself is a `<dialog-component>` (see dialog.js); this element
 * wires up the surrounding behaviour:
 *   - toggling the drawer from `[data-apc-menu-toggle]` triggers (which live
 *     outside this element, in the header), kept in sync via `aria-expanded`
 *   - "chaining" into the search / localization modals (close drawer, then open)
 *   - closing the drawer when an in-menu link is followed
 *   - submenu accordions that animate to their measured content height
 *   - mirroring the wishlist count (same source as the wishlist-icon component)
 *
 * All listeners are bound through a single AbortController so they are removed
 * cleanly on disconnect (e.g. theme-editor section re-renders).
 *
 * @extends {Component<{}>}
 */
class ApcMobileMenu extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#setupToggle(signal);
    this.#setupAriaSync(signal);
    this.#setupChainOpen(signal);
    this.#setupLinkClose(signal);
    this.#setupAccordions(signal);
    this.#setupWishlistCount(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * The drawer dialog-component.
   * @returns {(HTMLElement & { showDialog(): void; closeDialog(): Promise<void> | void; toggleDialog(): void }) | null}
   */
  get #dialog() {
    return /** @type {any} */ (this.querySelector('dialog-component'));
  }

  /**
   * Toggle triggers live outside this element (in the header), so delegate on
   * document. The drawer is resolved at click time so it survives re-renders.
   * @param {AbortSignal} signal
   */
  #setupToggle(signal) {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const toggle = target instanceof Element && target.closest('[data-apc-menu-toggle]');
        if (!toggle) return;

        event.preventDefault();
        this.#dialog?.toggleDialog();
      },
      { signal },
    );
  }

  /**
   * Keep every trigger's `aria-expanded` in sync with the drawer state.
   * @param {AbortSignal} signal
   */
  #setupAriaSync(signal) {
    const dialog = this.#dialog;
    if (!dialog) return;

    /** @param {boolean} expanded */
    const sync = (expanded) => {
      for (const btn of document.querySelectorAll('[data-apc-menu-toggle]')) {
        btn.setAttribute('aria-expanded', String(expanded));
      }
    };

    dialog.addEventListener('dialog:open', () => sync(true), { signal });
    dialog.addEventListener('dialog:close', () => sync(false), { signal });
  }

  /**
   * Chain into another modal (search, localization): fully close the drawer
   * first, then open the target. Both dialogs lock `document.body` scroll, so
   * letting the drawer finish its teardown first prevents its delayed scroll
   * reset from clobbering the modal that opens next.
   * @param {AbortSignal} signal
   */
  #setupChainOpen(signal) {
    const dialog = this.#dialog;
    if (!dialog) return;

    for (const btn of this.querySelectorAll('[data-apc-mm-open]')) {
      btn.addEventListener(
        'click',
        () => {
          const target = /** @type {any} */ (document.getElementById(btn.getAttribute('data-apc-mm-open') ?? ''));
          Promise.resolve(dialog.closeDialog()).then(() => {
            if (typeof target?.showDialog === 'function') target.showDialog();
          });
        },
        { signal },
      );
    }
  }

  /**
   * Close the drawer when an in-menu link is followed.
   * @param {AbortSignal} signal
   */
  #setupLinkClose(signal) {
    const dialog = this.#dialog;
    if (!dialog) return;

    for (const link of this.querySelectorAll('a[href]')) {
      link.addEventListener('click', () => dialog.closeDialog(), { signal });
    }
  }

  /**
   * Submenu accordions: animate `max-height` to the measured content height
   * (same approach as the PDP details accordion), then release the cap to
   * `none` once open so the panel can reflow freely.
   * @param {AbortSignal} signal
   */
  #setupAccordions(signal) {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

    for (const head of this.querySelectorAll('[data-apc-mm-acc]')) {
      const acc = head.closest('.apc-mobile-menu__acc');
      const body = acc?.querySelector('.apc-mobile-menu__acc-body');
      if (!acc || !(body instanceof HTMLElement)) continue;

      head.addEventListener(
        'click',
        () => {
          const willOpen = acc.getAttribute('data-expanded') !== 'true';
          acc.setAttribute('data-expanded', String(willOpen));
          head.setAttribute('aria-expanded', String(willOpen));
          body.inert = !willOpen;

          if (reducedMotion?.matches) {
            body.style.maxHeight = willOpen ? 'none' : '0px';
            return;
          }

          if (willOpen) {
            body.style.maxHeight = `${body.scrollHeight}px`;
            body.addEventListener(
              'transitionend',
              function onEnd(event) {
                if (event.propertyName !== 'max-height') return;
                body.removeEventListener('transitionend', onEnd);
                // Release the cap once open so the panel can reflow freely.
                if (acc.getAttribute('data-expanded') === 'true') body.style.maxHeight = 'none';
              },
              { signal },
            );
          } else {
            // Pin the current auto height to a pixel value, force a reflow,
            // then animate down to 0.
            body.style.maxHeight = `${body.scrollHeight}px`;
            void body.offsetHeight;
            body.style.maxHeight = '0px';
          }
        },
        { signal },
      );
    }
  }

  /**
   * Mirror the wishlist count (reads `window.apa.wishlist.count`, falling back
   * to localStorage, and updates on the `apa:wishlist:count` event).
   * @param {AbortSignal} signal
   */
  #setupWishlistCount(signal) {
    const counts = this.querySelectorAll('[data-apc-mm-wishlist-count]');
    if (!counts.length) return;

    const read = () => {
      if (typeof window.apa?.wishlist?.count === 'number') return window.apa.wishlist.count;
      try {
        return JSON.parse(localStorage.getItem('wishlistProducts') || '[]').length;
      } catch {
        return 0;
      }
    };

    /** @param {number} count */
    const render = (count) => {
      for (const el of counts) {
        if (count > 0) {
          el.textContent = `(${count < 100 ? count : '99+'})`;
          el.hidden = false;
        } else {
          el.textContent = '';
          el.hidden = true;
        }
      }
    };

    render(read());
    document.addEventListener(
      'apa:wishlist:count',
      (event) => render(/** @type {CustomEvent} */ (event).detail?.count || 0),
      { signal },
    );
  }
}

if (!customElements.get('apc-mobile-menu')) {
  customElements.define('apc-mobile-menu', ApcMobileMenu);
}
